import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { useFinancialReport } from '~/entrataReports';
import Dimmer from '+/Dimmer';
import { PrintDocument } from '../portfolio/PrintDocument';
import {
    Button
} from 'semantic-ui-react';
import {
    GridToolbarContainer
} from '@mui/x-data-grid';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';


const useStyles = makeStyles({
    table: {
        minWidth: 650
    },
    header: {
        fontWeight: 'bold',
        color: 'white'
    },
    bold: {
        fontWeight: 'bold'
    },
    cell: {
        fontWeight: 'bold',
        color: 'blue'
    }
});


const { format } = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
});


const asPerc = value => `${(value * 100).toFixed(2) }%`;

const transform = (payload, classes) => {

    const { vendors } = payload;
    const headers = vendors.map(vendor => [
        <TableCell className={classes.header}>{vendor}</TableCell>,
        <TableCell className={classes.header}>%</TableCell>
    ]).flat();

    const monthlySummary = (
        payload.data.map(property => property.components.at(-1).data)
            .reduce((acc, monthData) => {
                Object.entries(monthData).forEach(([vendor, amounts]) => {
                    const [dollarAmount] = amounts;
                    if (!acc[vendor]) {
                        acc[vendor] = 0;
                    }
                    acc[vendor] += dollarAmount;
                });

                return acc;
            }, {})
    );

    const totalMontly = Object.values(monthlySummary).reduce((a, c) => a += c, 0);
    const totalTotalMonthly = payload.data.reduce((a, c) => a += c.components.at(-1).total, 0);
    const monthlySummaryRow = (
        <TableRow>
            <TableCell className={classes.bold}>Selected Month Summary</TableCell>
            {Object.values(monthlySummary).map(value => (
                <>
                    <TableCell className={classes.bold}>{format(value)}</TableCell>
                    <TableCell className={classes.cell}>{asPerc(value / totalTotalMonthly)}</TableCell>
                </>
            ))}
            <TableCell className={classes.bold}>{format(totalMontly)}</TableCell>
            <TableCell className={classes.cell}>{asPerc(totalMontly / totalTotalMonthly)}</TableCell>

        </TableRow>
    );

    const ytdSummary = payload.data.reduce((acc, property) => {
        Object.entries(property.data).forEach(([vendor, amounts]) => {
            const [dollarAmount] = amounts;
            if (!acc[vendor]) {
                acc[vendor] = 0;
            }
            acc[vendor] += dollarAmount;
        });

        return acc;
    }, {});

    const totalYtd = Object.values(ytdSummary).reduce((a, c) => a += c, 0);
    const totalTotalYtd = payload.data.reduce((a, c) => a += c.total, 0);

    const ytdSummaryRow = (
        <TableRow style={{ background: '#D3D3D3' }}>
            <TableCell className={classes.bold}>YTD Summary</TableCell>
            {Object.values(ytdSummary).map(value => (
                <>
                    <TableCell className={classes.bold}>{format(value)}</TableCell>
                    <TableCell className={classes.cell}>{asPerc(value / totalTotalYtd)}</TableCell>
                </>
            ))}
            <TableCell className={classes.bold}>{format(totalYtd)}</TableCell>
            <TableCell className={classes.cell}>{asPerc(totalYtd / totalTotalYtd)}</TableCell>

        </TableRow>
    );

    const body = payload.data.map(({
        name, data, total, components, rnmTotal
    }) => {

        const month = components.at(-1);

        const subrows = [month].map(month => (
            <TableRow key={name + month.name}>
                <TableCell className={classes.bold} >{month.name}</TableCell>
                {vendors.map(vendor => [
                    <TableCell className={classes.bold} >{format(month.data[vendor][0])}</TableCell>,
                    <TableCell className={classes.cell}>{asPerc(month.data[vendor][1])}</TableCell>
                ]
                ).flat()}
                <TableCell className={classes.bold} >{format(month.rnmTotal[0])}</TableCell>
                <TableCell className={classes.cell}>{asPerc(month.rnmTotal[1])}</TableCell>
            </TableRow>
        ));

        const ytdRow = (
            <TableRow key={name} style={{ background: '#D3D3D3' }}>
                <TableCell className={classes.bold} >{name}</TableCell>
                {vendors.map(vendor => [
                    <TableCell className={classes.bold} >{format(data[vendor][0])}</TableCell>,
                    <TableCell className={classes.cell}>{asPerc(data[vendor][1])}</TableCell>
                ]
                ).flat()}
                <TableCell className={classes.bold} >{format(rnmTotal[0])}</TableCell>
                <TableCell className={classes.cell}>{asPerc(rnmTotal[1])}</TableCell>
            </TableRow>
        );

        return (
            <>
                {ytdRow}
                {subrows}
            </>
        );
    });

    return (
        <Table className={classes.table}>
            <TableHead>
                <TableRow style={{ backgroundColor: '#3093FE' }}>
                    <TableCell style={{ color: 'white' }}>Properties  --->  Vendors </TableCell>
                    {headers}
                    <TableCell style={{ color: 'white' }}>RnM Total</TableCell>
                    <TableCell style={{ color: 'white' }}>RnM %</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {body}
                {ytdSummaryRow}
                {monthlySummaryRow}
            </TableBody>
        </Table>
    );


};

const Print = props => {

    const { data } = props;

    const [shouldPrint, setShouldPrint] = React.useState(false);

    let PrintButton = (
        <Button

            style={{
                color: '#002144',
                backgroundColor: '#fafafa',
                fontSize: '12px',
                fontFamily: 'Lato',
                padding: 5,
                fontWeight: 100
            }} onClick={() => setShouldPrint(true)}>
            Prepare to Print
        </Button>
    );

    if (shouldPrint) {

        PrintButton = (
            <PrintDocument
                data={data}
                counts={100}
            />
        );
    }

    return (
        <GridToolbarContainer className='hide'>
            <LocalPrintshopIcon sx={{ color: '#002144' }}/>

            { PrintButton }

        </GridToolbarContainer>
    );
};

export default function SimpleTable() {


    const [date, setDate] = React.useState(null);
    const classes = useStyles();
    const { data, isLoading } = useFinancialReport(date, { enabled: !!date });


    if (isLoading) {
        return <Dimmer />;
    }

    return (
        <div>
            <div style={{
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <div>
                    <KeyboardDatePicker
                        label='Select a Date'
                        value={date}
                        onChange={setDate}
                        views={['month', 'year']}
                    />
                </div>
                {date && data && (

                    <div style={{
                        marginBottom: '10px',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <Print data={data} counts={20}/>
                    </div>


                )}
            </div>

            <div>
                {date && data && (
                    <div style={{ width: '100%' }}>
                        <TableContainer component={Paper}>
                            {transform(data, classes)}
                        </TableContainer>
                    </div>
                )}
            </div>
        </div>

    );
}
